import {
  ImageNode,
  SerializedImageNode,
} from '@/components/LexicalEditor/nodes/ImageNode';
import { NodeKey, type Spread } from 'lexical';
import React, { type JSX } from 'react';
import UploadImage from '@/components/LexicalEditor/nodes/UploadImageNode/UploadImage';

export type UploadImageStatus = 'loading' | 'success' | 'error';

export type SerializedUploadImageNode = Spread<
  {
    type: 'upload-image';
    status?: UploadImageStatus;
  },
  SerializedImageNode
>;

class UploadImageNode extends ImageNode {
  __status: UploadImageStatus;

  static getType(): string {
    return 'upload-image';
  }

  static clone(node: UploadImageNode): UploadImageNode {
    return new UploadImageNode(
      node.__src,
      node.__altText,
      node.__maxWidth,
      node.__width,
      node.__height,
      node.__showCaption,
      node.__caption,
      node.__captionsEnabled,
      node.__isFileUpload,
      node.__inline,
      node.__status,
      node.__key,
    );
  }

  static importJSON(
    serializedNode: SerializedUploadImageNode,
  ): UploadImageNode {
    const n: any = serializedNode;

    const safeStatus: UploadImageStatus =
      n.status ?? (n.isFileUpload ? 'loading' : 'success');

    return new UploadImageNode(
      n.src ?? '',
      n.altText ?? '',
      n.maxWidth ?? undefined,
      n.width ?? undefined,
      n.height ?? undefined,
      n.showCaption ?? false,
      n.caption ?? undefined,
      n.captionsEnabled ?? true,
      n.isFileUpload ?? false,
      n.inline ?? false,
      safeStatus,
    );
  }

  constructor(
    src: string,
    altText: string,
    maxWidth?: number,
    width?: 'inherit' | number,
    height?: 'inherit' | number,
    showCaption?: boolean,
    caption?: any,
    captionsEnabled?: boolean,
    isFileUpload?: boolean,
    inline?: boolean,
    status: UploadImageStatus = 'loading',
    key?: NodeKey,
  ) {
    super(
      src,
      altText,
      maxWidth,
      width,
      height,
      showCaption,
      caption,
      captionsEnabled,
      isFileUpload,
      inline,
      undefined, // format — 업로드 로딩 플레이스홀더는 정렬 미보유(최종 이미지로 교체됨)
      key,
    );
    this.__status = status;
  }

  setStatus(status: UploadImageStatus) {
    (this.getWritable() as UploadImageNode).__status = status;
  }

  getStatus(): UploadImageStatus {
    return (this.getLatest() as UploadImageNode).__status;
  }

  decorate(): JSX.Element {
    if (this.__status === 'loading' || this.__status === 'error') {
      return (
        <UploadImage
          src={this.__src}
          altText={this.__altText}
          status={this.__status}
          nodeKey={this.getKey()}
        />
      );
    }

    return super.decorate();
  }

  exportJSON(): SerializedUploadImageNode {
    const json = super.exportJSON() as SerializedImageNode;
    return {
      ...json,
      type: 'upload-image',
      status: this.__status,
    };
  }
}

export default UploadImageNode;

export function $createUploadImageNode({
  src,
  altText,
  status = 'loading',
  inline = false,
}: {
  src: string;
  altText: string;
  status?: UploadImageStatus;
  inline?: boolean;
}): UploadImageNode {
  return new UploadImageNode(
    src,
    altText,
    undefined,
    undefined,
    undefined,
    false,
    undefined,
    true,
    true,
    inline,
    status,
  );
}
